import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Phone, UserRound, Users } from "lucide-react";
import { API, adminApi, errorMessage } from "../../lib/api";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { LocationPicker } from "../landing/LocationPicker";
import { RolePicker } from "../landing/RolePicker";
import { inr } from "../landing/usePricing";

const PhotoThumb = ({ w, scope }) => {
  const [open, setOpen] = useState(false);
  const src = `${API}/employer/workers/${w.id}/photo`;
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={`${scope}worker-photo-btn-${w.id}`} className="shrink-0 cursor-zoom-in" aria-label="View photo">
        <img src={src} alt={w.name} loading="lazy" data-testid={`${scope}worker-photo-${w.id}`} className="h-12 w-10 rounded border border-white/10 object-cover transition-transform hover:scale-105" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`${scope}worker-photo-zoom-${w.id}`} className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl border-0 bg-white p-4">
          <DialogTitle className="text-base font-semibold text-slate-800">{w.name} · {w.job_role}</DialogTitle>
          <img src={src} alt={w.name} className="max-h-[70vh] w-full rounded-xl object-contain" />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const WorkerCard = ({ w, fee, onUnlock, onHire, scope = "" }) => (
  <article data-testid={`${scope}worker-card-${w.id}`} className="border border-white/10 bg-[#0F172A] p-3 text-xs">
    <div className="flex items-start gap-2.5">
      {w.has_photo ? <PhotoThumb w={w} scope={scope} /> : (
        <span className="flex h-12 w-10 shrink-0 items-center justify-center rounded border border-white/10 bg-[#1E293B]"><UserRound className="h-5 w-5 text-slate-500" /></span>
      )}
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-white">{w.name}</p>
        <p className="mt-0.5 text-slate-400">{w.job_role} · Age {w.age}{w.height_cm ? ` · ${w.height_cm} cm` : ""}</p>
        {(w.gender || w.marital_status) && <p className="capitalize text-slate-500">{[w.gender, w.marital_status].filter(Boolean).join(" · ")}</p>}
      </div>
    </div>
    <p className="mt-1 text-slate-500">{w.district}, {w.state}{w.expected_salary ? ` · ${inr(w.expected_salary)}/mo` : ""}</p>
    {w.skills?.length > 0 && <p className="mt-1 truncate text-slate-400">{w.skills.join(" · ")}</p>}
    <p className="mt-1.5 flex items-center gap-1.5 font-mono text-slate-300">
      {w.unlocked ? <Phone className="h-3.5 w-3.5 text-emerald-400" /> : <Lock className="h-3.5 w-3.5 text-slate-500" />}{w.unlocked ? `+91 ${w.mobile}` : w.mobile}
    </p>
    {w.hired ? (
      <p data-testid={`${scope}worker-hired-${w.id}`} className="mt-2 w-full border border-emerald-500/30 bg-emerald-500/10 py-1 text-center font-semibold text-emerald-300">Hired</p>
    ) : w.joining_pending ? (
      <p data-testid={`${scope}worker-joining-${w.id}`} className="mt-2 w-full border border-amber-500/30 bg-amber-500/10 py-1 text-center font-semibold text-amber-300">Joining OTP pending</p>
    ) : w.unlocked ? (
      <button onClick={() => onHire(w)} data-testid={`${scope}worker-hire-${w.id}`} className="mt-2 w-full border border-emerald-500/60 py-1 font-semibold text-emerald-300 hover:bg-emerald-500/10">Mark as hired</button>
    ) : (
      <button onClick={() => onUnlock(w)} data-testid={`${scope}worker-unlock-${w.id}`} className="mt-2 w-full bg-orange-500 py-1 font-semibold text-white hover:bg-orange-600">
        Unlock contact ({fee ? inr(fee) : "Free"})
      </button>
    )}
  </article>
);

export const WorkerSearch = ({ onChange, refresh }) => {
  const [f, setF] = useState({ state: "", district: "", department: "", job_role: "" });
  const [data, setData] = useState({ items: [], unlock_fee: 0, total: 0, all_total: 0, summary: [] });
  const [page, setPage] = useState(1);
  const load = useCallback((pg = 1) => {
    const params = { ...Object.fromEntries(Object.entries(f).filter(([, v]) => v)), page: pg };
    adminApi.get("/employer/workers", { params })
      .then((r) => { setPage(pg); setData((old) => (pg > 1 ? { ...r.data, items: [...old.items, ...r.data.items] } : r.data)); })
      .catch((e) => toast.error(errorMessage(e)));
  }, [f]);
  useEffect(() => { load(1); }, [load, refresh]);

  const act = (path, msg) => adminApi.post(path).then(() => { toast.success(msg); load(1); onChange(); }).catch((e) => toast.error(errorMessage(e)));
  const chip = (on) => `flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm ${on ? "border-orange-500 bg-orange-500 font-semibold text-white" : "border-white/15 bg-[#0F172A] text-slate-300 hover:border-orange-500"}`;

  return (
    <section data-testid="worker-search" className="space-y-4">
      <h2 className="font-heading text-2xl font-bold text-white">Find verified employees <span className="text-base font-normal text-slate-500" data-testid="worker-search-total">({data.total} · {f.district || f.state || "All India"})</span></h2>
      <div className="grid grid-cols-1 gap-4 border border-white/10 bg-[#0F172A] p-5 sm:grid-cols-2 lg:grid-cols-4">
        <LocationPicker prefix="emp-search" state={f.state} district={f.district} onChange={(s, d) => setF({ ...f, state: s, district: d })} />
        <RolePicker prefix="emp-search" department={f.department} jobRole={f.job_role} onChange={(d, r) => setF({ ...f, department: d, job_role: r })} />
      </div>
      <div className="flex flex-wrap gap-2" data-testid="worker-search-summary">
        <button onClick={() => setF({ ...f, state: "", district: "" })} data-testid="worker-search-all-india" className={chip(!f.state)}>All India: {data.all_total}</button>
        {data.summary.map((s) => (
          <button key={s.name} onClick={() => setF({ ...f, ...(f.state ? { district: s.name } : { state: s.name, district: "" }) })}
            data-testid={`worker-search-chip-${s.name.replace(/\s+/g, "-").toLowerCase()}`} className={chip(f.district === s.name)}>
            <Users className="h-3.5 w-3.5 text-orange-500" />{s.name}: <b>{s.count}</b>
          </button>
        ))}
      </div>
      {data.items.length === 0 && <p data-testid="worker-search-empty" className="py-8 text-center text-slate-500">No KYC-approved employees match these filters yet.</p>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {data.items.map((w) => (
          <WorkerCard key={w.id} w={w} fee={data.unlock_fee}
            onUnlock={(x) => act(`/employer/workers/${x.id}/unlock`, "Contact unlocked")}
            onHire={(x) => act(`/employer/workers/${x.id}/hire`, "Joining OTP generated. Ask the employee for the 4-digit code at site.")} />
        ))}
      </div>
      {data.items.length < data.total && (
        <button onClick={() => load(page + 1)} data-testid="worker-search-more" className="mx-auto block rounded-full border border-orange-500 px-6 py-2 text-sm font-semibold text-orange-500 hover:bg-orange-500 hover:text-white">
          Show more employees ({data.total - data.items.length} more)
        </button>
      )}
    </section>
  );
};
